import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    Select,
    VStack,
    useToast,
    Text,
    HStack,
    Box,
    Flex,
    Icon,
    Link,
    FormHelperText,
    useColorModeValue,
} from "@chakra-ui/react";
import { Tooltip } from "./components/Tip";
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { FiLink, FiPlus } from 'react-icons/fi';
import { alpha, useCalTheme } from './components/ui';
import { ADD_EXTERNAL_CALENDAR } from './queries';

interface ExternalCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// The feed's own colour choices — data the person picks, not page chrome.
const CALENDAR_COLORS = [
  { value: '#4285F4', label: 'Blue' },
  { value: '#DB4437', label: 'Red' },
  { value: '#F4B400', label: 'Yellow' },
  { value: '#0F9D58', label: 'Green' },
  { value: '#AB47BC', label: 'Purple' },
  { value: '#00ACC1', label: 'Cyan' },
  { value: '#FF7043', label: 'Orange' },
  { value: '#9E9E9E', label: 'Gray' },
];

/** Subscribe to an outside calendar feed (ICS). POLISH v1 (2026-09-25): kit tokens, 48px fields, readable help. */
const ExternalCalendarModal: React.FC<ExternalCalendarModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const toast = useToast();
  const t = useCalTheme();
  // A modal needs a solid surface in both modes (the kit's card surface is glass).
  const modalBg = useColorModeValue('white', 'gray.900');
  const labelProps = { fontSize: 'md', fontWeight: 'semibold', color: t.text } as const;
  const helperProps = { fontSize: 'sm', color: t.muted } as const;

  const [name, setName] = useState('');
  const [icsUrl, setIcsUrl] = useState('');
  const [color, setColor] = useState('#4285F4');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [addExternalCalendar] = useMutation(ADD_EXTERNAL_CALENDAR, {
    onCompleted: () => {
      toast({
        title: 'External calendar added',
        description: 'The calendar has been added successfully',
        status: 'success',
        duration: 3000,
      });
      onSuccess?.();
      handleClose();
    },
    onError: (error) => {
      toast({
        title: 'Error adding calendar',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
      setIsSubmitting(false);
    },
  });

  const handleClose = () => {
    setName('');
    setIcsUrl('');
    setColor('#4285F4');
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!name || !icsUrl) {
      toast({
        title: 'Missing information',
        description: 'Please provide both a name and calendar URL',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Basic validation for ICS URL
    if (!icsUrl.includes('.ics') && !icsUrl.includes('calendar')) {
      toast({
        title: 'Invalid URL',
        description: 'Please provide a valid calendar URL (ICS format)',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    await addExternalCalendar({
      variables: {
        input: {
          name,
          type: 'SHARED_EXTERNAL',
          iCalUrl: icsUrl,
          color,
          isPublic: true,
          visibility: 'SHARED',
          sharedFromName: name,
        },
      },
      context: {
        headers: {
          'x-tenant-id': localStorage.getItem('tenantId') || '',
        },
      },
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={{ base: "full", md: "lg" }} scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />
      <ModalContent bg={modalBg} borderWidth="1px" borderColor={t.border} borderRadius={{ base: 0, md: '2xl' }}>
        <ModalHeader pr={14}>
          <HStack spacing={3} align="center">
            <Flex flexShrink={0} w={10} h={10} borderRadius="lg" bgImage={t.accentGradient} align="center" justify="center">
              <Icon as={FiLink} color="white" />
            </Flex>
            <Text fontSize="xl" color={t.text} letterSpacing="-0.01em">Add an outside calendar</Text>
          </HStack>
          <Text fontSize="md" fontWeight="normal" color={t.muted} mt={2}>
            Show a published calendar from Google Calendar, Outlook, or any service that gives an ICS/iCal link.
            It's read-only — nothing here is written back to it.
          </Text>
        </ModalHeader>
        <Tooltip label="Close without adding anything. What you typed is discarded.">
          <ModalCloseButton size="lg" w="44px" h="44px" top={3} right={3} />
        </Tooltip>
        <ModalBody>
          <VStack spacing={5} align="stretch">
            <FormControl isRequired>
              <FormLabel {...labelProps}>Calendar name</FormLabel>
              <Input
                size="lg"
                focusBorderColor={t.primary}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Team calendar, Public holidays"
              />
              <FormHelperText {...helperProps}>A friendly name to recognise this calendar by.</FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel {...labelProps}>Calendar link (ICS)</FormLabel>
              <Input
                size="lg"
                focusBorderColor={t.primary}
                value={icsUrl}
                onChange={(e) => setIcsUrl(e.target.value)}
                placeholder="https://calendar.example.com/calendar.ics"
                inputMode="url"
              />
              <FormHelperText {...helperProps}>The ICS/iCal link from the calendar provider.</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel {...labelProps}>Colour</FormLabel>
              <HStack spacing={3}>
                <Select size="lg" focusBorderColor={t.primary} value={color} onChange={(e) => setColor(e.target.value)} flex={1} minW={0}>
                  {CALENDAR_COLORS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
                <Box
                  aria-hidden
                  flexShrink={0}
                  w="48px"
                  h="48px"
                  bg={color}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={t.borderStrong}
                />
              </HStack>
            </FormControl>

            <Box p={4} borderRadius="xl" bg={t.raised} borderWidth="1px" borderColor={t.border}>
              <Text fontSize="md" fontWeight="semibold" color={t.text} mb={2}>
                Where to find the link
              </Text>
              <VStack align="stretch" spacing={2} fontSize="sm" color={t.muted}>
                <Box>
                  <Text as="span" fontWeight="semibold" color={t.text}>Google Calendar: </Text>
                  <Link href="https://support.google.com/calendar/answer/37103" isExternal color={t.primary} textDecoration="underline">
                    Settings → Integrate → Secret address in iCal format
                    <ExternalLinkIcon mx="2px" />
                  </Link>
                </Box>
                <Box>
                  <Text as="span" fontWeight="semibold" color={t.text}>Outlook: </Text>
                  <Link
                    href="https://support.microsoft.com/en-us/office/share-your-calendar-in-outlook-on-the-web-7ecef8ae-139c-40d9-bae2-a23977ee58d5"
                    isExternal
                    color={t.primary}
                    textDecoration="underline"
                  >
                    Calendar → Share → Publish calendar → ICS link
                    <ExternalLinkIcon mx="2px" />
                  </Link>
                </Box>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter gap={3} flexDirection={{ base: 'column-reverse', sm: 'row' }} alignItems="stretch" justifyContent="flex-end">
          <Tooltip label="Close without adding anything. What you typed is discarded.">
            <Button variant="ghost" size="lg" minH="48px" color={t.muted} onClick={handleClose}>
              Cancel
            </Button>
          </Tooltip>
          <Tooltip whiteSpace="pre-line" label={"Subscribe to this external calendar feed.\n\nIts events are pulled in and shown alongside yours. It is READ-ONLY — nothing you do here is written back to the other calendar."}>
            <Box>
              <Button
                w="100%"
                size="lg"
                minH="48px"
                bgImage={t.accentGradient}
                color="white"
                _hover={{ opacity: 0.92, boxShadow: `0 8px 24px ${alpha(t.primary, 0.35)}` }}
                _active={{ opacity: 0.85 }}
                leftIcon={<FiPlus />}
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Adding…"
              >
                Add calendar
              </Button>
            </Box>
          </Tooltip>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ExternalCalendarModal;
